import React, { useMemo } from "react";
import {
  aggregationIntervalToDateValue,
  aggregationIntervalWithDurationFromDateValue,
  aggregationPeriodFromPresets,
  aggregationPeriodLabel,
  type AggregationPeriod,
} from "../../../../domain/aggregation/period";
import WidgetCard from "../WidgetCard";
import { css, cx } from "@linaria/core";
import ChartLossesRepartition from "./ChartLossesRepartition";

import t from "../../../../i18n";
import WidgetSelectorHeader, { widgetSelectorHeader } from "../WidgetSelectorHeader";
import { useTranslations } from "../../../../api/useTranslations";
import { spacings } from "@ui/Theme";
import { useEvents } from "../../../../api/useEvents";
import { useAggregationIntervalFromPeriodState } from "../useAggregationIntervalFromPeriodState";
import useGroups from "../../../../api/useGroups";
import { eventAggregationPeriodPresets } from "../../../../domain/aggregation/preset";
import { useActivePowerPerformanceIndicator } from "../../../../api/usePerformanceIndicators";
import NoDataPeriodPlaceholderWidget from "../NoDataPeriodPlaceholderWidget";
import { useSettings } from "../../../../api/useSettings";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import { useProject } from "../../../../api/useProjects";

const LossesRepartitionWidget = ({
  projectId,
  projectTimezone,
  className,
  sharedAggregationPeriod,
}: {
  projectId: string;
  projectTimezone: string;
  className?: string;
  sharedAggregationPeriod: boolean;
}) => {
  const translations = useTranslations();
  const settings = useSettings();
  const events = useEvents(projectId, settings.MANUAL_EVENTS_DEV_MODE_ENABLED);
  const { activeAnalyses, isStreaming } = useActiveAnalysesStream(projectId);
  const settingsAggregationPeriod = settings.AGGREGATION_PERIOD as AggregationPeriod;
  const activePowerPerformanceIndicator = useActivePowerPerformanceIndicator(projectId);
  const project = useProject(projectId);
  if (!project) {
    throw new Error("no project");
  }

  const [aggregationInterval, setAggregationInterval] = useAggregationIntervalFromPeriodState({
    projectId,
    projectTimezone,
    aggregationPeriod: settingsAggregationPeriod,
    shared: sharedAggregationPeriod,
    from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
  });

  const groups = useGroups(projectId);
  const aggregationPeriodPresets = useMemo(
    () =>
      eventAggregationPeriodPresets({
        timezone: projectTimezone,
        groups,
        limit: {
          from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
          to: new Date(),
        },
      }),
    [groups, project.analysesAggregate?.earliestActiveAnalysis?.date, projectTimezone],
  );

  const aggregationPeriod = aggregationPeriodFromPresets({
    dateValues: aggregationIntervalToDateValue(aggregationInterval, projectTimezone),
    presets: aggregationPeriodPresets,
  });

  return (
    <WidgetCard className={cx(className, widgetCardWithHeaderMargin, lossesRepartitionWidget)}>
      <WidgetSelectorHeader
        title={t("project.fault_event.live.widget.losses_repartition.title", {
          translations,
        })}
        tooltipText={t(
          "project.fault_event.live.widget.losses_repartition.tooltip",
          translations,
          aggregationPeriodLabel(
            aggregationPeriod ?? settingsAggregationPeriod,
          ).toLocaleLowerCase(),
        )}
        aggregationIntervalDateValue={aggregationIntervalToDateValue(
          aggregationInterval,
          projectTimezone,
        )}
        aggregationPeriodPresets={aggregationPeriodPresets}
        onAggregationIntervalChange={interval =>
          setAggregationInterval(
            aggregationIntervalWithDurationFromDateValue(
              interval,
              projectTimezone,
              aggregationPeriodPresets,
            ),
          )
        }
        hidePeriod={false}
      />
      {activePowerPerformanceIndicator ? (
        <ChartLossesRepartition
          projectId={projectId}
          aggregationInterval={aggregationInterval}
          activeAnalyses={activeAnalyses}
          performanceIndicator={activePowerPerformanceIndicator}
          loading={isStreaming}
          events={events}
        />
      ) : (
        <div className={centeredPlaceholder}>
          <NoDataPeriodPlaceholderWidget />
        </div>
      )}
    </WidgetCard>
  );
};

export default LossesRepartitionWidget;

//#region CSS
const lossesRepartitionWidget = css`
  height: 100%;
  flex: 1;
`;
const widgetCardWithHeaderMargin = css`
  .${widgetSelectorHeader} {
    margin-bottom: ${spacings.m};
  }
`;
const centeredPlaceholder = css`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

//#endregion CSS
